/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { workloadIdentitiesAdmitSubject } from "../funcs/workloadIdentitiesAdmitSubject.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { WorkloadIdentityPolicy } from "../models/components/workloadidentitypolicy.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  AdmitWorkloadSubjectRequest,
  AdmitWorkloadSubjectSecurity,
} from "../models/operations/admitworkloadsubject.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type AdmitWorkloadSubjectMutationVariables = {
  request: AdmitWorkloadSubjectRequest;
  security?: AdmitWorkloadSubjectSecurity | undefined;
  options?: RequestOptions;
};

export type AdmitWorkloadSubjectMutationData = WorkloadIdentityPolicy;

export type AdmitWorkloadSubjectMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * admitSubject workloadIdentities
 *
 * @remarks
 * Admit a subject one of the trusted issuers asserts, and assign the agent it inherits its policy from. Both happen in one transaction: a subject admitted without an agent is refused at the token endpoint, so that half-configured state is not reachable. Requires workload:write.
 */
export function useAdmitWorkloadSubjectMutation(
  options?: MutationHookOptions<
    AdmitWorkloadSubjectMutationData,
    AdmitWorkloadSubjectMutationError,
    AdmitWorkloadSubjectMutationVariables
  >,
): UseMutationResult<
  AdmitWorkloadSubjectMutationData,
  AdmitWorkloadSubjectMutationError,
  AdmitWorkloadSubjectMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildAdmitWorkloadSubjectMutation(client, options),
    ...options,
  });
}

export function mutationKeyAdmitWorkloadSubject(): MutationKey {
  return ["@gram/client", "workloadIdentities", "admitSubject"];
}

export function buildAdmitWorkloadSubjectMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: AdmitWorkloadSubjectMutationVariables,
  ) => Promise<AdmitWorkloadSubjectMutationData>;
} {
  return {
    mutationKey: mutationKeyAdmitWorkloadSubject(),
    mutationFn: function admitWorkloadSubjectMutationFn({
      request,
      security,
      options,
    }): Promise<AdmitWorkloadSubjectMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(workloadIdentitiesAdmitSubject(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
